import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';

import { PaginatedData } from '@shared/models/paginated-data';
import { urlEndPoints } from '../config/end-points';
import { allAvailableNurses } from '../interfaces/profile.interfaces';
import { Calendar, ProfileCalendarsResponseDto } from '../models/calendar.model';
import { Period } from '../models/period.model';
import { Profile, SearchProfileInput } from '../models/profile.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileDal {
  constructor(private httpClient: HttpClient) {}

  getProfiles(
    page: number | null,
    limit: number | null,
    name: string,
    noUrgences?: boolean,
    searchType?: string,
    data?: any,
    options?: { noHospitalsLookup: boolean }
  ): Observable<Profile[]> {
    const commonParams = 'page=' + (page ? page : 1) + '&limit=' + limit;
    const endPoint =
      urlEndPoints.profiles +
      '?' +
      commonParams +
      (name !== '' ? '&search=' + name : '') +
      (noUrgences ? '&noUrgences=true' : '') +
      (searchType ? '&searchType=' + searchType : '') +
      (options && options.noHospitalsLookup ? '&noHospitalsLookup=' + options.noHospitalsLookup : '') +
      (data ? '&data=' + data : '');
    return this.httpClient.get<Profile[]>(endPoint);
  }

  getNurseReportPDF(
    profileId: string,
    hospitalId: string,
    periodStart: string,
    periodEnd: string,
    showAnnex: boolean,
    showIndividualExperienceCharts: boolean,
    showRank: boolean,
    data: any
  ): Observable<Blob> {
    const endPoint =
      urlEndPoints.profiles +
      'getNurseReportPDF/' +
      '?profile=' +
      profileId +
      '&hospital=' +
      hospitalId +
      '&periodStart=' +
      periodStart +
      '&periodEnd=' +
      periodEnd +
      '&showAnnex=' +
      showAnnex +
      '&showIndivExp=' +
      showIndividualExperienceCharts +
      '&showRank=' +
      showRank;
    return this.httpClient.post<Blob>(endPoint, data, { responseType: 'blob' as 'json' });
  }

  getAvailableAndOutOfRoomProfiles(
    hospitalId: string,
    date: Date | string,
    positions: string[],
    residency: string[],
    seniority: string[]
  ) {
    const endPoint = urlEndPoints.profiles + 'availableOutOfRoomProfiles';
    const queryParams =
      '?hospital=' +
      hospitalId +
      '&date=' +
      date +
      '&position=' +
      positions +
      '&residency=' +
      residency +
      '&seniority=' +
      seniority;
    return this.httpClient.get<Profile[]>(endPoint + queryParams);
  }

  getSurgeonsByDayAvailability(
    date: string,
    opening: boolean,
    page: number,
    pageSize: number,
    searchValue: string,
    population: string = ''
  ): Observable<Profile[]> {
    return this.httpClient.post<Profile[]>(
      urlEndPoints.profiles + 'hrsuiteFilter?date=' + date + '&opening=' + opening,
      { searchValue: searchValue ? searchValue : '', page, pageSize, population }
    );
  }

  getProfilesWithTimes(
    position?: string,
    day?: string,
    period?: Period,
    type: string = 'allDay',
    smartplanning?: boolean
  ): Observable<Calendar[]> {
    let baseUrl = urlEndPoints.profiles;

    if (position) {
      baseUrl = `${urlEndPoints.profiles}by/isgarde/${position}`;
    }

    let params = new HttpParams();
    if (day) {
      params = params.append('jour', day);
    }
    let param1, param2;
    if (type == 'allDay') {
      param1 = 'from';
      param2 = 'to';
    } else if (type == 'morning') {
      param1 = 'morningFrom';
      param2 = 'morningTo';
    } else {
      param1 = 'afternoonFrom';
      param2 = 'afternoonTo';
    }

    if (period && period.from) {
      params = params.append(param1, period.from);
    }
    if (period && period.to) {
      params = params.append(param2, period.to);
    }
    if (smartplanning) {
      params = params.append('smartplanning', smartplanning);
    }
    return this.httpClient.get<Calendar[]>(baseUrl, { params });
  }

  updateUserProfile(profile: Profile): Observable<Profile> {
    return this.httpClient.put<Profile>(`${urlEndPoints.profiles}${profile._id}`, profile);
  }

  createProfileWithSkills(profile: Profile): Observable<Profile> {
    return this.httpClient.post<Profile>(`${urlEndPoints.profiles}`, profile);
  }

  createDefaultSpecialtySurgeon(profile: Profile, specialty: string): Observable<Profile> {
    return this.httpClient.post<Profile>(`${urlEndPoints.profiles}defaultSpecialtySurgeon/${specialty}`, profile);
  }

  editProfile(profile: Profile): Observable<Profile> {
    return this.httpClient.put<Profile>(`${urlEndPoints.profiles}${profile._id}`, profile);
  }

  editProfileMail(profileId: string, mail: string): Observable<Profile> {
    return this.httpClient.put<Profile>(`${urlEndPoints.profiles}${profileId}`, { mail });
  }

  editVacationNeeds(profile: Profile, originalSpe: string): Observable<any> {
    return this.httpClient.put<any>(
      urlEndPoints.profiles + 'updateVacations/' + profile._id + '?originalSpecialty=' + originalSpe,
      profile
    );
  }

  editProfileWithSkills(profile: any): Observable<Profile> {
    return this.httpClient.put<Profile>(`${urlEndPoints.profiles}withSkills/${profile.profile._id}`, profile);
  }

  getProfileWeekAvailability(profileId: string, date: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.calendars + 'byProfile/' + profileId + '/' + date);
  }

  getProfilesCalendars(
    profiles: string[],
    fromDate: string,
    toDate: string,
    options: { hrSuiteProject?: boolean },
    scenario?: string,
    campaign?: string
  ): Observable<ProfileCalendarsResponseDto[]> {
    return this.httpClient.post<ProfileCalendarsResponseDto[]>(
      urlEndPoints.calendars +
        'byProfiles' +
        '?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        (options.hrSuiteProject ? '&hrSuiteProject=' + options.hrSuiteProject : '') +
        '&scenario=' +
        scenario +
        (campaign ? '&campaign=' + campaign : ''),
      { profiles }
    );
  }

  getProfilesByPosition(
    position: string,
    page: number | null,
    limit: number | null,
    name: string,
    justTitular: boolean = false,
    allProfiles: boolean = false,
    noUrgences: boolean = false,
    seniorities?: string,
    isNight: boolean = false,
    keepDefaultSpecialtySurgeons: boolean = true,
    profileOnTop: boolean = false,
    population?: string,
    profilesIds?: string,
    residency?: string,
    options?: { noHospitalsLookup: boolean },
    specialties?: string,
    services?: string | undefined,
    includesTimeAlert: boolean = false
  ): Observable<any> {
    let endPoint;
    if (allProfiles) {
      endPoint =
        urlEndPoints.profiles +
        'by/' +
        position +
        '?' +
        (name !== '' ? 'search=' + name : '') +
        (justTitular ? '&residency=Titulaire' : '') +
        (noUrgences ? '&noUrgences=true' : '') +
        (seniorities ? `&seniorities=${seniorities}` : '') +
        (isNight ? `&isNight=${isNight}` : '') +
        (profileOnTop ? `&profileOnTop=${profileOnTop}` : '') +
        (population ? `&population=${population}` : '') +
        (profilesIds ? `&profilesIds=${profilesIds}` : '') +
        (residency ? `&residency=${residency}` : '') +
        (options && options.noHospitalsLookup ? '&noHospitalsLookup=' + options.noHospitalsLookup : '') +
        (specialties ? `&specialties=${specialties}` : '') +
        (services ? `&services=${services}` : '') +
        (includesTimeAlert ? '&includesTimeAlert=true' : '&includesTimeAlert=false') + // Nouveau paramètre ajouté ici
        `&keepDefaultSpecialtySurgeons=${keepDefaultSpecialtySurgeons}`;
    } else {
      endPoint =
        urlEndPoints.profiles +
        'by/' +
        position +
        '?page=' +
        (page ? page : 1) +
        '&limit=' +
        limit +
        (name !== '' ? '&search=' + name : '') +
        (justTitular ? '&residency=Titulaire' : '') +
        (noUrgences ? '&noUrgences=true' : '') +
        (seniorities ? `&seniorities=${seniorities}` : '') +
        (isNight ? `&isNight=${isNight}` : '') +
        (profileOnTop ? `&profileOnTop=${profileOnTop}` : '') +
        (population ? `&population=${population}` : '') +
        (profilesIds ? `&profilesIds=${profilesIds}` : '') +
        (residency ? `&residency=${residency}` : '') +
        (options && options.noHospitalsLookup ? '&noHospitalsLookup=' + options.noHospitalsLookup : '') +
        (specialties ? `&specialties=${specialties}` : '') +
        (services ? `&services=${services}` : '') +
        (includesTimeAlert ? '&includesTimeAlert=true' : '&includesTimeAlert=false') +
        `&keepDefaultSpecialtySurgeons=${keepDefaultSpecialtySurgeons}`;
    }
    return this.httpClient.get<any>(endPoint);
  }

  getProfilesByPositionFromHospital(
    hospital: string | undefined,
    position: string | undefined,
    page: number | undefined,
    limit: number | undefined,
    name: string,
    residency?: string,
    allProfiles: boolean = false,
    noUrgences: boolean = false,
    seniorities?: string,
    population?: string,
    isNight: boolean = false,
    isNoUserAssociate?: boolean,
    specialties?: string
  ): Observable<any> {
    let endPoint;
    if (allProfiles) {
      endPoint =
        urlEndPoints.profiles +
        'by/' +
        position +
        '?hospital=' +
        hospital +
        (name !== '' ? '&search=' + name : '') +
        (residency ? `&residency=${residency}` : '') +
        (noUrgences ? '&noUrgences=true' : '') +
        (seniorities ? `&seniorities=${seniorities}` : '') +
        (population ? `&population=${population}` : '') +
        (isNight ? `&isNight=${isNight}` : '') +
        (isNoUserAssociate ? '&isNoUserAssociate=' + isNoUserAssociate : '') +
        (specialties ? `&specialties=${specialties}` : '');
    } else {
      endPoint =
        urlEndPoints.profiles +
        'by/' +
        position +
        '?hospital=' +
        hospital +
        '&page=' +
        (page ? page : 1) +
        '&limit=' +
        limit +
        (name !== '' ? '&search=' + name : '') +
        (residency ? `&residency=${residency}` : '') +
        (noUrgences ? '&noUrgences=true' : '') +
        (seniorities ? `&seniorities=${seniorities}` : '') +
        (population ? `&population=${population}` : '') +
        (isNight ? `&isNight=${isNight}` : '') +
        (isNoUserAssociate ? '&isNoUserAssociate=' + isNoUserAssociate : '') +
        (specialties ? `&specialties=${specialties}` : '');
    }
    return this.httpClient.get<any>(endPoint);
  }

  searchProfiles(searchProfileInput: SearchProfileInput): Observable<PaginatedData<Profile>> {
    const { page, limit, search, population, seniorities, formationsGroups, specialties, includeDesactivated } =
      searchProfileInput;

    let endPoint = `${urlEndPoints.profiles}search?page=${page}&limit=${limit}`;

    if (search) {
      endPoint += `&search=${search}`;
    }

    if (population) {
      endPoint += `&population=${population}`;
    }

    if (seniorities) {
      endPoint += `&seniorities=${seniorities}`;
    }

    if (formationsGroups) {
      endPoint += `&formationsGroups=${formationsGroups}`;
    }

    if (specialties) {
      endPoint += `&specialties=${specialties}`;
    }

    if (includeDesactivated) {
      endPoint += `&includeDesactivated=true`;
    }

    return this.httpClient.get<any>(endPoint);
  }

  getProfilesByPositionResidency(
    position: string,
    page: number,
    limit: number,
    name: string,
    residency?: string,
    allNurses: boolean = false,
    noUrgences: boolean = false
  ): Observable<any> {
    let endPoint;
    if (allNurses) {
      endPoint =
        urlEndPoints.profiles +
        'by/' +
        position +
        '?' +
        (name !== '' ? 'search=' + name : '') +
        (residency ? '&residency=' + residency : '') +
        (noUrgences ? '&noUrgences=true' : '');
    } else {
      endPoint =
        urlEndPoints.profiles +
        'by/' +
        position +
        '?page=' +
        (page ? page : 1) +
        '&limit=' +
        limit +
        (name !== '' ? '&search=' + name : '') +
        (residency ? '&residency=' + residency : '') +
        (noUrgences ? '&noUrgences=true' : '');
    }
    return this.httpClient.get<PaginatedData<Profile>>(endPoint);
  }

  getProfilesByPositionResidencyFromHospital(
    hospital: string,
    position: string,
    page: number,
    limit: number,
    name: string,
    residency?: string,
    allNurses: boolean = false,
    noUrgences: boolean = false
  ): Observable<any> {
    let endPoint =
      urlEndPoints.profiles +
      'by/' +
      position +
      '?' +
      'hospital=' +
      hospital +
      (name !== '' ? '&search=' + name : '') +
      (residency ? '&residency=' + residency : '') +
      (noUrgences ? '&noUrgences=true' : '');

    if (!allNurses) {
      endPoint += '&page=' + (page ? page : 1) + '&limit=' + limit;
    }

    return this.httpClient.get<any>(endPoint);
  }

  getProfilesWithUserByPositionResidencyFromHospital(
    hospital: string,
    position: string,
    page: number | null,
    limit: number | null,
    name: string,
    residency?: string,
    allNurses: boolean = false,
    noUrgences: boolean = false
  ): Observable<any> {
    let endPoint =
      urlEndPoints.profiles +
      'with-user/' +
      'by/' +
      position +
      '?' +
      'hospital=' +
      hospital +
      (name !== '' ? '&search=' + name : '') +
      (residency ? '&residency=' + residency : '') +
      (noUrgences ? '&noUrgences=true' : '');

    if (!allNurses) {
      endPoint += '&page=' + (page ? page : 1) + '&limit=' + limit;
    }
    return this.httpClient.get<any>(endPoint);
  }

  //not used
  getProfilesWithAvailabilityByPositionResidencyFromHospital(
    date: string,
    hospital: string,
    position: string,
    page: number,
    limit: number,
    name: string,
    residency?: string,
    allNurses: boolean = false,
    noUrgences: boolean = false,
    isNight: boolean = false
  ): Observable<any> {
    let endPoint =
      urlEndPoints.profiles +
      'with-availability/' +
      'by/' +
      position +
      '?' +
      'date=' +
      date +
      '&hospital=' +
      hospital +
      (name !== '' ? '&search=' + name : '') +
      (residency ? '&residency=' + residency : '') +
      (noUrgences ? '&noUrgences=true' : '') +
      ('&isNight=' + isNight);

    if (!allNurses) {
      endPoint += '&page=' + (page ? page : 1) + '&limit=' + limit;
    }
    return this.httpClient.get<any>(endPoint);
  }

  deleteDefaultSpecialtySurgeons(specialtyId: string): Observable<any> {
    return this.httpClient.delete<Profile>(urlEndPoints.profiles + 'defaultSpecialtySurgeon/' + specialtyId);
  }

  getProfilesBySpecialty(position: string): Observable<any> {
    const endPoint = urlEndPoints.profiles + 'ratedBySpecialty/' + position;
    return this.httpClient.get<any>(endPoint);
  }

  getProfilesBySpecialtyFromHospital(position: string, hospital: string): Observable<any> {
    const endPoint = urlEndPoints.profiles + 'ratedBySpecialty/' + position + '?hospital=' + hospital;
    return this.httpClient.get<any>(endPoint);
  }

  getProfilesBySpecialtyInDateFromHospital(position: string, hospital: string, date: string): Observable<any> {
    const endPoint =
      urlEndPoints.profiles + 'ratedBySpecialtyWithDate/' + position + '?hospital=' + hospital + '&date=' + date;
    return this.httpClient.get<any>(endPoint);
  }

  getProfilesByDayAvailability(
    position: string | undefined,
    date: string,
    descriptions: string[],
    isNight: boolean,
    page?: number,
    pageSize?: number,
    searchValue?: string,
    population?: string,
    profilesIds?: string,
    services?: string,
    residency?: string[],
    seniorities?: string,
    scenario?: string,
    campaignId?: string
  ): Observable<Profile[]> {
    const body = this.getBody(
      descriptions,
      date,
      position,
      page,
      pageSize,
      searchValue,
      isNight,
      population,
      profilesIds,
      services,
      residency,
      seniorities
    );
    return this.httpClient.post<Profile[]>(
      urlEndPoints.profiles + `hrsuiteFilter?scenario=${scenario}&campaignId=${campaignId}`,
      body
    );
  }

  getBody(
    descriptions: string[],
    date: string,
    position: string | undefined,
    page?: number,
    pageSize?: number,
    searchValue?: string,
    isNight?: boolean,
    population?: string,
    profilesIds?: string,
    services?: string,
    residency?: string[],
    seniorities?: string
  ): any {
    const body: any = { position, date, isNight, searchValue: searchValue ? searchValue : '', page, pageSize };
    if (residency) {
      let filteredResidency = [...residency];

      if (descriptions.includes('Titulaire') && !descriptions.includes('Vacataire')) {
        filteredResidency = filteredResidency.filter((res: any) => res !== 'Vacataire');
      }

      if (descriptions.includes('Vacataire') && !descriptions.includes('Titulaire')) {
        filteredResidency = filteredResidency.filter((res: any) => res !== 'Titulaire');
      }

      body.residency = filteredResidency;
    }

    // if (position == ANESTHETIST || position == NURSE) {
    if (this.isFilterAvaSelected(descriptions)) {
      body.descriptions = [descriptions[0]];
      descriptions.splice(0, 1);
    } else {
      body.descriptions = ['AVAILABLE', 'ABSENT'];
    }

    if (this.isFilterStatusSelected(descriptions)) {
      body.residency = descriptions[0];
    }
    // } else {
    //   body.descriptions = descriptions;
    // }

    if (population) {
      body.population = population;
    }

    if (profilesIds) {
      body.profilesIds = profilesIds;
    }

    if (services) {
      body.services = services;
    }

    if (seniorities) {
      body.seniorities = seniorities;
    }

    return body;
  }

  isFilterStatusSelected(descriptions: string[]): boolean {
    return descriptions.includes('Titulaire') || descriptions.includes('Vacataire');
  }

  isFilterAvaSelected(descriptions: string[]): boolean {
    return descriptions.includes('AVAILABLE') || descriptions.includes('ABSENT');
  }

  getOutOfRoomNurses(date: string): Observable<Profile[]> {
    return this.httpClient.get<Profile[]>(urlEndPoints.profiles + 'outOfRoomNurses?date=' + date);
  }

  getOutOfRoomIades(date: string): Observable<Profile[]> {
    return this.httpClient.get<Profile[]>(urlEndPoints.profiles + 'outOfRoomIades?date=' + date);
  }

  getAllAvailableNurses(date: string): Observable<allAvailableNurses[]> {
    return this.httpClient.get<allAvailableNurses[]>(urlEndPoints.profiles + 'allAvailableNurses?date=' + date);
  }

  getReachableProfiles(date: string, position: string) {
    return this.httpClient.get<any>(urlEndPoints.profiles + 'reachableProfiles/by/' + position + '?date=' + date);
  }

  getOutOfRoomNursesFromTo(
    startDate: string,
    endDate: string,
    scenario?: string,
    campaign?: string
  ): Observable<any[]> {
    return this.httpClient.get<Profile[]>(
      urlEndPoints.profiles +
        'outOfRoomNurses/interval?from=' +
        startDate +
        '&to=' +
        endDate +
        '&scenario=' +
        scenario +
        (campaign ? '&campaign=' + campaign : '')
    );
  }

  getOutOfRoomIadesFromTo(startDate: string, endDate: string, scenario?: string, campaign?: string): Observable<any[]> {
    return this.httpClient.get<Profile[]>(
      urlEndPoints.profiles +
        'outOfRoomIades/interval?from=' +
        startDate +
        '&to=' +
        endDate +
        '&scenario=' +
        scenario +
        (campaign ? '&campaign=' + campaign : '')
    );
  }

  getConsultationsFromTo(startDate: string, endDate: string, scenario?: string, campaign?: string): Observable<any[]> {
    return this.httpClient.get<Profile[]>(
      urlEndPoints.profiles +
        'getConsultations/interval?from=' +
        startDate +
        '&to=' +
        endDate +
        '&scenario=' +
        scenario +
        (campaign ? '&campaign=' + campaign : '')
    );
  }

  getGardeFromTo(startDate: string, endDate: string, scenario?: string, campaign?: string): Observable<any[]> {
    return this.httpClient.get<Profile[]>(
      urlEndPoints.profiles +
        'getGardes/interval?from=' +
        startDate +
        '&to=' +
        endDate +
        '&scenario=' +
        scenario +
        (campaign ? '&campaign=' + campaign : '')
    );
  }

  getProfileById(profileId: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.profiles + profileId);
  }

  getProfileHRSuiteRecap(profileId: string, startDate: string, endDate: string, hospital?: string): Observable<any> {
    return this.httpClient.get<Profile>(
      urlEndPoints.ibodeGraphs +
        'hrsuiteRecap/' +
        profileId +
        '?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        (hospital ? '&hospital=' + hospital : '')
    );
  }

  getProfileHRSuiteRHcodesRecap(
    profileId: string,
    startDate: string,
    endDate: string,
    hospital?: string
  ): Observable<any> {
    return this.httpClient.get<Profile>(
      urlEndPoints.ibodeGraphs +
        'hrsuiteRecap/HRcodes/' +
        profileId +
        '?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        (hospital ? '&hospital=' + hospital : '')
    );
  }

  addProfile(profile: Profile): Observable<Profile> {
    return this.httpClient.post<Profile>(urlEndPoints.profiles, profile);
  }

  getAllProfilesIncludeDeleted(
    hospitalId?: string,
    page?: number,
    limit?: number,
    positions?: string,
    name?: string,
    sortField?: string,
    sortDirection?: '-1' | '1',
    seniorities?: string,
    specialty?: string,
    allSpecialties?: string,
    residencies?: string
  ): Observable<Profile[]> {
    let params = new HttpParams();

    if (hospitalId) {
      params = params.set('hospital', hospitalId);
    }
    if (page) {
      params = params.set('page', page.toString());
    }
    if (limit) {
      params = params.set('limit', limit.toString());
    }
    if (positions) {
      params = params.set('positions', positions);
    }
    if (name) {
      params = params.set('name', name);
    }
    if (sortField) {
      params = params.set('sortField', sortField);
    }
    if (sortDirection) {
      params = params.set('sortDirection', sortDirection);
    }
    if (seniorities) {
      params = params.set('seniorities', seniorities);
    }
    if (specialty) {
      params = params.set('specialty', specialty);
    }
    if (allSpecialties) {
      params = params.set('allSpecialties', allSpecialties);
    }
    if (residencies) {
      params = params.set('residencies', residencies);
    }
    return this.httpClient.get<Profile[]>(urlEndPoints.profiles + 'allProfiles', { params });
  }

  getprofileImageByscrapping(surgeonId: string, hospitalId: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.profiles + 'ScrappingSurgeon/by/' + surgeonId + '/' + hospitalId);
  }

  softDeleteProfile(idProfile: string, selectedHospitals?: string[] | undefined): Observable<Profile> {
    //checker le fonctionnement via profiles-details
    return this.httpClient.put<Profile>(urlEndPoints.profiles + 'softDeleteProfile/' + idProfile, {
      selectedHospitals,
    });
  }

  softRestoreProfile(idProfile: string, selectedHospitals?: string[] | undefined): Observable<Profile> {
    return this.httpClient.put<Profile>(urlEndPoints.profiles + 'softRestoreProfile/' + idProfile, {
      selectedHospitals,
    });
  }

  hardRestoreProfile(idProfile: string): Observable<Profile> {
    return this.httpClient.put<Profile>(urlEndPoints.profiles + 'hardRestoreProfile/' + idProfile, {});
  }

  modifyProfile(profile: Profile): Observable<Profile> {
    return this.httpClient.put<Profile>(urlEndPoints.profiles + profile._id, profile);
  }

  deleteProfile(idProfile: string): Observable<Profile> {
    return this.httpClient.delete<Profile>(urlEndPoints.profiles + 'delete/' + idProfile);
  }

  getAverages(
    startDate: string,
    endDate: string,
    isGarde: boolean = false,
    hospitalId?: string,
    residency?: string,
    iadeProfilesIds?: string,
    positions?: string,
    scenario?: string,
    campaign?: string,
    effectifType?: string,
    requestedDocuments?: {
      anesthNeeds?: boolean;
      effectif?: boolean;
      nightEffectif?: boolean;
      nightGuard?: boolean;
      nightNeeds?: boolean;
      nurseNeeds?: boolean;
      residency?: boolean;
    }
  ): Observable<any> {
    return this.httpClient.post<any>(
      urlEndPoints.calendars +
        'effectifByDay?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        (isGarde ? '&isGarde=true' : '') +
        (hospitalId ? '&hospital=' + hospitalId : '') +
        (residency ? '&residency=' + residency : '') +
        (iadeProfilesIds ? '&iadeProfilesIds=' + iadeProfilesIds : '') +
        (positions ? '&positions=' + positions : '') +
        (scenario ? '&scenario=' + scenario : '') +
        (campaign ? '&campaign=' + campaign : ''),
      { requestedDocuments, effectifType }
    );
  }

  getNeedsCoverageBySkills(
    startDate: string,
    endDate: string,
    positions?: string,
    populationId?: string,
    scenario?: string,
    campaign?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.calendars +
        'needsCoverageBySkill?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&populationId=' +
        populationId +
        (positions ? '&positions=' + positions : '') +
        '&scenario=' +
        scenario +
        '&campaign=' +
        campaign
    );
  }

  getAveragesForMultipleDates(
    dateIntervals: Array<{ fromDate: string; toDate: string }>,
    isGarde: boolean = false,
    hospitalId?: string,
    residency?: string,
    iadeProfilesIds?: string,
    positions?: string,
    scenario?: string,
    campaign?: string,
    effectifType?: string,
    requestedDocuments?: {
      anesthNeeds?: boolean;
      effectif?: boolean;
      nightEffectif?: boolean;
      nightGuard?: boolean;
      nightNeeds?: boolean;
      nurseNeeds?: boolean;
      residency?: boolean;
    }
  ): Observable<any> {
    const queryParams = new URLSearchParams();

    if (isGarde) {
      queryParams.append('isGarde', 'true');
    }
    if (hospitalId) {
      queryParams.append('hospital', hospitalId);
    }
    if (residency) {
      queryParams.append('residency', residency);
    }
    if (iadeProfilesIds) {
      queryParams.append('iadeProfilesIds', iadeProfilesIds);
    }
    if (positions) {
      queryParams.append('positions', positions);
    }
    if (scenario) {
      queryParams.append('scenario', scenario);
    }
    if (campaign) {
      queryParams.append('campaign', campaign);
    }

    const queryString = queryParams.toString();
    const url = `${urlEndPoints.calendars}effectifByDayBatch${queryString ? '?' + queryString : ''}`;

    return this.httpClient.post<any>(url, {
      dateIntervals,
      requestedDocuments,
      effectifType,
    });
  }

  getNeeds(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.calendars + 'effectifByDayDA?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getProfilesSummary(
    position: string,
    residency: string,
    seniority: string,
    fromDate: string,
    toDate: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.profiles +
        `vacatairesSummary?position=${position}&residency=${residency}&seniority=${seniority}&fromDate=${fromDate}&toDate=${toDate}`
    );
  }

  getListWishes(profileId: string, startDate: string, endDate: string): Observable<any[]> {
    return this.httpClient.get<any[]>(
      urlEndPoints.realtimes + 'listwishes/' + profileId + `?fromDate=${startDate}&toDate=${endDate}`
    );
  }

  getWorkingTime(profileId: string, startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.realtimes + 'all/' + profileId + `?fromDate=${startDate}&toDate=${endDate}`
    );
  }

  getSurgeonsBySpecialty(date?: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.profiles + 'surgeonsBySpecialty' + (date ? `?date=${date}` : ''));
  }

  getAllSurgeons(hospital: string, date?: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.profiles + 'surgeonsBySpecialty' + '?hospital=' + hospital + (date ? `&date=${date}` : '')
    );
  }

  getAllSurgeonsOnlyUser(hospital: string, date?: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.profiles + 'surgeonsOnlyUserBySpecialty' + '?hospital=' + hospital + (date ? `&date=${date}` : '')
    );
  }

  createCustomJobDescription(profileId: string, position: string, toExcludeFromIndividual: string[]): Observable<any> {
    return this.httpClient.post<any>(
      urlEndPoints.profiles + 'createCustomJobDescription/' + profileId + `?position=${position}`,
      { toExcludeFromIndividual }
    );
  }

  diffuseConstraint(constraintId: string, model: string, profiles: string[], position: string): Observable<any> {
    return this.httpClient.post<any>(
      urlEndPoints.profiles + 'diffuseConstraint/' + `?id=${constraintId}&model=${model}&position=${position}`,
      { profiles }
    );
  }

  addConstraint(profiles: string[], constraint: any, model: string, position: string): Observable<any> {
    return this.httpClient.post<any>(urlEndPoints.profiles + 'addConstraint/', {
      profiles,
      constraint,
      model,
      position,
    });
  }

  getProfilesWithoutTimeAlert(position: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.profiles + 'withoutTimeAlert/?position=' + position);
  }

  getAllProfilesInList(profiles: string[]): Observable<Profile[] | undefined> {
    return this.httpClient.get<Profile[] | undefined>(urlEndPoints.profiles + 'getAllProfilesInList/', {
      params: { profiles },
    });
  }
}
